import { AxiosRequestConfig } from 'axios';
import { httpConfig } from 'src/lib/modules/http';
import { Api } from 'src/api';
import {
  CancelSubscriptionRequest,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CreateStripeCustomerRequest,
  CreateStripeCustomerResponse,
  CreateSubscriptionRequest,
  CreateUpgradePortalSessionRequest,
  CreateUpgradePortalSessionResponse,
  ExpiredSubscriptionSummary,
  PromotionCodeResponse,
  ReactivateSubscriptionResponse,
  StripeIntentResponse,
  StripePaymentIntentRequest,
  StripeProductsResponse,
  SubscriptionObject,
  SubscriptionPlanChangeResponse,
  ValidatePromotionCodeRequest,
} from 'src/lib/services/api/subscription-api/subscription-client.types';
import { InterceptableAxiosRequestConfig } from 'src/api/Http/Http.types';
import { url } from 'src/lib/utils';

const baseUrl = `${httpConfig.options.baseURL}`;

const products = (config?: AxiosRequestConfig): Promise<StripeProductsResponse> =>
  Api.http.get(`${baseUrl}/stripe/products`, config);

const validatePromoCode = (
  request: ValidatePromotionCodeRequest,
  config?: InterceptableAxiosRequestConfig,
): Promise<PromotionCodeResponse> =>
  Api.http.post(`${baseUrl}/stripe/validate-promotion-code`, request, config);

const createStripeIntent = (
  request: StripePaymentIntentRequest,
  config?: AxiosRequestConfig,
): Promise<StripeIntentResponse> =>
  Api.http.post(`${baseUrl}/stripe/create-stripe-intent`, request, config);

const createPayment = (
  request: StripePaymentIntentRequest,
  config?: AxiosRequestConfig,
): Promise<StripeIntentResponse> =>
  Api.http.post(`${baseUrl}/stripe/create-payment`, request, config);

const createSubscription = (
  request: CreateSubscriptionRequest,
  config?: AxiosRequestConfig,
): Promise<SubscriptionObject> =>
  Api.http.post(`${baseUrl}/stripe/create-subscription`, request, config);

const createStripeCustomer = (
  request: CreateStripeCustomerRequest,
  config?: AxiosRequestConfig,
): Promise<CreateStripeCustomerResponse> =>
  Api.http.post(`${baseUrl}/stripe/create-stripe-customer`, request, config);

const createCustomerSession = (
  config?: AxiosRequestConfig,
): Promise<{ client_secret: string }> =>
  Api.http.post(`${baseUrl}/stripe/create-customer-session`, config);

const createBillingPortalSession = (
  returnUrl?: string,
  config?: AxiosRequestConfig,
): Promise<{ url: string }>  =>
  Api.http.post(`${baseUrl}/stripe/create-billing-portal-session`, { return_url: returnUrl }, config);

const createUpgradePortalSession = (
  request: CreateUpgradePortalSessionRequest,
  config?: AxiosRequestConfig,
): Promise<CreateUpgradePortalSessionResponse> =>
  Api.http.post(`${baseUrl}/stripe/create-upgrade-portal-session`, request, config);

const createCheckoutSession = (
  request: CreateCheckoutSessionRequest,
  config?: AxiosRequestConfig,
): Promise<CreateCheckoutSessionResponse> =>
  Api.http.post(`${baseUrl}/stripe/create-checkout-session`, request, config);

// Expired window (#2781): the meta line, and the one Reactivate button
const getExpiredSummary = (config?: AxiosRequestConfig): Promise<ExpiredSubscriptionSummary> =>
  Api.http.get(`${baseUrl}/stripe/expired-summary`, config);

// The returned URL pays or buys without a login: never log it or send it to Sentry/PostHog
const reactivateSubscription = (
  returnUrl: string,
  config?: AxiosRequestConfig,
): Promise<ReactivateSubscriptionResponse> =>
  Api.http.post(`${baseUrl}/stripe/reactivate`, { return_url: returnUrl }, config);

// Change plan on the Expired window: same response shape as Reactivate, same rules for the URL
const changeExpiredPlan = (
  priceId: string,
  returnUrl: string,
  config?: AxiosRequestConfig,
): Promise<ReactivateSubscriptionResponse> =>
  Api.http.post(
    `${baseUrl}/stripe/change-plan`,
    { price_id: priceId, return_url: returnUrl },
    config,
  );

// Re-syncs the company from Stripe while the client pays in another tab
const refreshExpiredState = (
  config?: AxiosRequestConfig,
): Promise<{ access_state: 'normal' | 'expired' | 'pricing_table' | null }> =>
  Api.http.post(`${baseUrl}/stripe/expired-refresh`, {}, config);

const abortPayment = (paymentId: string, config?: AxiosRequestConfig): Promise<void> =>
  Api.http.post(`${baseUrl}/stripe/abort-payment`, { payment_id: paymentId }, config);

const cancel = (
  model: string,
  data: CancelSubscriptionRequest,
  config?: AxiosRequestConfig,
): Promise<SubscriptionObject> =>
  Api.http.delete(url.insertArguments(`${baseUrl}/subscriptions/{model}/cancel`, { model }), {
    data,
    ...config,
  });

const reactivate = (model: string, config?: AxiosRequestConfig): Promise<SubscriptionObject> =>
  Api.http.post(
    url.insertArguments(`${baseUrl}/subscriptions/{model}/reactivate`, { model }),
    config,
  );

const getUpgrade = (
  model: string,
  config?: AxiosRequestConfig,
): Promise<SubscriptionPlanChangeResponse> =>
  Api.http.get(`${baseUrl}/subscriptions/${model}/upgrade`, config);

const upgrade = (
  model: string,
  config?: AxiosRequestConfig,
): Promise<SubscriptionPlanChangeResponse> =>
  Api.http.post(`${baseUrl}/subscriptions/${model}/upgrade`, {}, config);

const getDowngrade = (
  model: string,
  config?: AxiosRequestConfig,
): Promise<SubscriptionPlanChangeResponse> =>
  Api.http.get(`${baseUrl}/subscriptions/${model}/downgrade`, config);

const scheduleDowngrade = (
  model: string,
  config?: AxiosRequestConfig,
): Promise<SubscriptionPlanChangeResponse> =>
  Api.http.post(`${baseUrl}/subscriptions/${model}/schedule-downgrade`, {}, config);

const cancelDowngrade = (model: string, config?: AxiosRequestConfig): Promise<SubscriptionObject> =>
  Api.http.post(`${baseUrl}/subscriptions/${model}/cancel-downgrade`, {}, config);

export {
  products,
  validatePromoCode,
  createStripeIntent,
  createPayment,
  createSubscription,
  createStripeCustomer,
  createCustomerSession,
  createBillingPortalSession,
  createUpgradePortalSession,
  createCheckoutSession,
  getExpiredSummary,
  reactivateSubscription,
  changeExpiredPlan,
  refreshExpiredState,
  abortPayment,
  cancel,
  reactivate,
  getUpgrade,
  upgrade,
  getDowngrade,
  scheduleDowngrade,
  cancelDowngrade,
};
